import React, { useCallback, useEffect, useRef, useState } from "react";

// What a reply just did, as a small +N or −N beside the tab that holds it —
// Today, Cupboard, and each meal's tab — for MARK_MS, then gone. A reply
// never switches the tab: the marks are how a change on a tab not in view is
// noticed, and the person opens it if they want to.
//
// Added is +, removed is −. A changed entry or item is neither: it is not new
// to where it is, and the panel's own numbers already show it.
export const MARK_MS = 15000;

const SIGN = { added: 1, removed: -1 };

// One kept bot message as marks: { scope, sign } each, where scope is "today",
// "cupboard" or "meal:<meal>".
export const marksOf = (message) => {
  const marks = [];
  for (const line of message?.entries || []) {
    const sign = SIGN[line.action];
    if (!sign) continue;
    marks.push({ scope: "today", sign });
    if (line.meal) marks.push({ scope: `meal:${line.meal}`, sign });
  }
  for (const line of message?.items || []) {
    const sign = SIGN[line.action];
    if (sign) marks.push({ scope: "cupboard", sign });
  }
  return marks;
};

// The marks on one scope, as counts.
export const tally = (marks, scope) => {
  const mine = (marks || []).filter((m) => m.scope === scope);
  return {
    plus: mine.filter((m) => m.sign > 0).length,
    minus: mine.filter((m) => m.sign < 0).length,
  };
};

let sequence = 0;

// The marks showing now, and `mark(message)` to add a reply's. Each reply's
// marks go together, MARK_MS after it came back.
export const useMarks = () => {
  const [marks, setMarks] = useState([]);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const mark = useCallback((message) => {
    const fresh = marksOf(message).map((m) => ({ ...m, id: ++sequence }));
    if (!fresh.length) return;
    const ids = new Set(fresh.map((m) => m.id));
    setMarks((all) => [...all, ...fresh]);
    timers.current.push(setTimeout(() => setMarks((all) => all.filter((m) => !ids.has(m.id))), MARK_MS));
  }, []);

  return [marks, mark];
};

// The label itself: "+2", "−1", or both.
export const Mark = ({ marks, scope, testid }) => {
  const { plus, minus } = tally(marks, scope);
  if (!plus && !minus) return null;
  return (
    <span className="noshchat-mark" data-testid={testid}>
      {plus ? <span className="noshchat-mark__plus">+{plus}</span> : null}
      {minus ? <span className="noshchat-mark__minus">−{minus}</span> : null}
    </span>
  );
};
